var IconButton = require("app/ui/IconButton"),
    Popup      = require("enyo-ui/Popup"),
    dispatcher = require("enyo/dispatcher"),
    icons      = require("app/theme/icons"),
    kind       = require("enyo/kind");

require("app/ui/Popup.less");
require("app/ui/Scrim.less");
require("app/theme/IconButton--close.less");

module.exports = kind(/** @lends  app.ui.Popup.prototype */ {

    /**
     * @private
     */
    name: "app.ui.Popup",

    /**
     * @private
     */
    kind: Popup,

    /**
     * @private
     */
    classes: "app-ui-popup",

    /**
     * Whether the popup is currently capturing events.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isCapturing: false,

    /**
     * The theme to use for this popup.
     *
     * @type  {?string}
     * @public
     */
    theme: null,

    /**
     * Boolean that controls whether or not a scrim will be displayed.
     *
     * @type     {boolean}
     * @default true
     * @public
     */
    useScrim: true,

    /**
     * @private
     */
    scrimOptions: {
        classes: "app-ui-scrim"
    },

    /**
     * Popups float by default.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    floating: true,

    /**
     * Whether the popup should capture events.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    captureEvents: false,

    /**
     * Whether to allow captured taps to propagate.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    propagateCapturedTap: false,

    /**
     * The path to the tapContainer control. Any taps outside the bounds
     * of the tapContainer will close the popup.
     *
     * @type     {?string}
     * @default  null
     * @public
     */
    tapContainerPath: null,

    /**
     * Whether to use a close button.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    showCloseButton: true,

    /**
     * @private
     */
    windowComponents: [{
        name:    "client",
        classes: "app-ui-popup__client"
    }, {
        name:    "closeButton",
        kind:    IconButton,
        classes: "app-ui-popup__close-button",
        icon:    icons["app.ui.Popup.closeButton"],
        theme:   "close",
        ontap:   "onCloseButtonTap"
    }],

    /**
     * @private
     */
    observers: {
        toggleCapturing: ["captureEvents", "absoluteShowing", "isCapturing"]
    },

    /**
     * @private
     */
    handlers: {
        ondown: "onCloseButtonDown",
        onup:   "onCloseButtonUp"
    },

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createWindow();
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.themeChanged();
            this.showCloseButtonChanged();
        };
    }),

    /**
     * @method
     * @private
     */
    createScrim: kind.inherit(function(sup) {
        return function() {
            var scrim;

            scrim = sup.apply(this, arguments);

            if (scrim && this.layout && this.layout.onScrimCreated) {
                this.layout.onScrimCreated();
            }

            return scrim;
        };
    }),

    /**
     * @protected
     */
    createWindow: function() {
        var windowComponents = this.windowComponents;

        this.createComponents(windowComponents);
        this.controlParent = this.$.client;
    },

    /**
     * Set theme classes based on current theme.
     *
     * @param  {string}  previousTheme  The previous theme
     * @private
     */
    themeChanged: function(previousTheme) {
        var currentTheme = this.get("theme"),
            baseClass    = "app-ui-popup",
            $client      = this.$.client;

        if (previousTheme && previousTheme !== currentTheme) {
            this.removeClass(baseClass + "--" + previousTheme);
            $client.removeClass(baseClass + "__client--" + previousTheme);
        }

        if (currentTheme && currentTheme !== previousTheme) {
            this.addClass(baseClass + "--" + currentTheme);
            $client.addClass(baseClass + "__client--" + currentTheme);
        }
    },

    /**
     * @private
     */
    showCloseButtonChanged: function() {
        var showCloseButton = this.get("showCloseButton"),
            $closeButton    = this.$.closeButton;

        if (!$closeButton) {
            return;
        }

        if (!showCloseButton && $closeButton.hasNode()) {
            $closeButton.removeNodeFromDom();
            $closeButton.teardownRender();
        }

        $closeButton.set("canGenerate", showCloseButton);

        if (showCloseButton && this.hasNode() && !$closeButton.hasNode()) {
            $closeButton.render();
        }
    },

    // Capturing behavior
    // --------------------------------

    /**
     * @private
     */
    toggleCapturing: function() {
        var captureEvents   = this.captureEvents,
            absoluteShowing = this.absoluteShowing;

        if (captureEvents && absoluteShowing) {
            this.startCapturing();
        }

        if (!captureEvents || !absoluteShowing) {
            this.stopCapturing();
        }
    },

    /**
     * @private
     */
    startCapturing: function() {
        if (!this.isCapturing) {
            dispatcher.capture(this, {
                ontap: "maybeCloseOnCapturedTap"
            });
            this.isCapturing = true;
        }
    },

    /**
     * @private
     */
    stopCapturing: function() {
        if (this.isCapturing) {
            dispatcher.release(this);
            this.isCapturing = false;
        }
    },

    /**
     * Close the popup when tapping outside of it.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Event}         event   The event
     * @returns  {boolean}       Stop propagation of the tap
     * @protected
     */
    maybeCloseOnCapturedTap: function(sender, event) {
        var tapContainerPath = this.get("tapContainerPath") || null,
            tapContainer     = tapContainerPath ? this.get(tapContainerPath) : this,
            tapContainerNode = tapContainer.hasNode();

        if (tapContainerNode && !tapContainerNode.contains(event.target)) {
            this.close();

            return !this.get("propagateCapturedTap");
        }

        return false;
    },

    // Visibility
    // --------------------------------

    /**
     * @param {enyo.Control} sender The sender of the event
     * @private
     */
    onCloseButtonDown: function(sender) {
        if (sender === this.$.closeButton) {
            this.set("isCloseButtonDown", true);
        }
    },

    /**
     * @private
     */
    onCloseButtonUp: function() {
        if (this.get("isCloseButtonDown")) {
            this.set("isCloseButtonDown", false);
        }
    },

    /**
     * @returns  {boolean}  Stop propagation of the tap
     * @private
     */
    onCloseButtonTap: function() {
        this.close();

        return true;
    },

    /**
     * Stop capturing on destroy
     *
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.stopCapturing();
            sup.apply(this, arguments);
        };
    })

});
